<template>
  <ontos-dialog :model-value="true" max-width="420px" @update:model-value="onDialogInput">
    <v-card>
      <!-- O cabeçalho de janela do app, montado pelo `utils/heading` como o de toda outra: o
           item no título, o campo que se troca no subtítulo, e a cor do item no acento. Antes
           aqui ia só o nome do campo, e a janela aberta de um card entre dezenas não dizia de
           QUEM era o valor. -->
      <v-card-title>
        <ontos-page-header v-bind="heading" :level="2">
          <template #controls>
            <ontos-dialog-actions @closed="$emit('closed')"/>
          </template>
        </ontos-page-header>
      </v-card-title>

      <!-- O que a lista abaixo está escolhendo, no alto do conteúdo.

           Com mais de uma dimensão é um CAMPO: trocar aqui troca as opções inteiras, e o nome
           da escolhida já aparece dentro dele. Com uma só o campo seria enfeite — uma caixa com
           uma opção —, e sobra o nome como rótulo, pela mesma classe de rótulo que os campos
           que não são input do Vuetify usam.

           Não vai no subtítulo da janela: ali vai a coleção, como em toda ficha do app, senão
           duas janelas do mesmo item diriam coisas diferentes no mesmo lugar. -->
      <v-card-text v-if="dimensions.length > 1" class="pt-0 pb-0">
        <v-select :model-value="dimension" :items="dimensions"
                  item-title="name" item-value="key"
                  :label="$tt('dimension')" density="compact" variant="outlined" hide-details
                  @update:model-value="$emit('update:dimension', $event)"/>
      </v-card-text>
      <v-card-text v-else-if="field" class="pt-0 pb-0">
        <span class="ontos-field-label">{{ field }}</span>
      </v-card-text>

      <!-- O valor atual aparece na lista, marcado e desabilitado, em vez de ficar de fora:
           mostrar todos é mais direto, e dispensa um bloco à parte só para dizer onde o item
           está.

           MÚLTIPLA, a lista vira caixas e o atual vem marcado e DESMARCÁVEL — é assim que se
           tira um valor, e é por isso que ali nada fica desabilitado. Sem as caixas, cada
           clique gravaria uma vez e não haveria gesto nenhum para "tirar". -->
      <v-list density="compact" class="option-list">
        <v-list-item v-for="option of options" :key="option.id"
                     :disabled="!multiple && isChosen(option)"
                     @click="onPick(option)">
          <template #prepend v-if="multiple">
            <v-checkbox-btn :model-value="isChosen(option)" density="compact"
                            class="flex-grow-0 mr-2" @update:model-value="toggle(option)"/>
          </template>
          <!-- O valor inteiro pelo ColoredValue — nome incluído: a forma dele muda com a
               natureza do valor, e o nome faz parte do arranjo nas três. -->
          <ontos-colored-value :option="option" :form="form"/>
          <template #append v-if="!multiple && isChosen(option)">
            <v-icon size="small">mdi-check</v-icon>
          </template>
        </v-list-item>
      </v-list>

      <!-- Salvar só na múltipla: ali a escolha é um CONJUNTO, e não existe conjunto pronto no
           meio de marcar caixas. Com um valor só o clique já diz tudo, e um botão a mais seria
           um passo a mais para repetir o que já foi dito. -->
      <ontos-dialog-action-row v-if="multiple">
        <v-btn size="small" color="primary" @click="$emit('save', chosen)">
          <v-icon size="small" start>mdi-floppy</v-icon>
          {{ $tt('save') }}
        </v-btn>
      </ontos-dialog-action-row>
    </v-card>
  </ontos-dialog>
</template>
<script lang="ts">
import {Component, Vue, Prop, Watch, toNative} from 'vue-facing-decorator'
import DialogActions from "@/components/shared/DialogActions.vue";
import DialogActionRow from "@/components/shared/DialogActionRow.vue";
import ColoredValue from "@/components/shared/ColoredValue.vue";
import {ColoredOption, ValueForm} from '@/types/ColoredOption'
import {Heading} from '@/utils/heading'
import {asArray} from '@/utils/arrayUtils'

/** Uma dimensão como este diálogo precisa dela: um nome para mostrar, uma chave para devolver. */
export type DimensionChoice = { key: string, name: string }

/**
 * Escolher um valor entre opções fixas, cada uma com a sua bolinha.
 *
 * Um componente só para os dois lugares que oferecem isso: o card do Kanban, onde é o caminho
 * preciso de mover um item, e a ficha da instância. A ficha tinha a mesma lista numa fileira de
 * botões "Mudar para X" — o mesmo conteúdo, escrito de outro jeito, que ia se soltando deste a
 * cada mudança.
 *
 * Recebe `ColoredOption[]` e não o fluxo: no Kanban a dimensão pode ser uma seleção ou um
 * relacionamento, e o que ele move é o valor DELA. É o mesmo contrato que já unificou o campo de
 * seleção e o campo de situação — o componente não pergunta de onde a cor veio.
 *
 * **Não conhece `Dimension`**, nem quando troca de dimensão: do select sai só a chave, e quem a
 * resolve em opções, cores e cardinalidade é o host, que já tem as dimensões em mão. Foi o que
 * permitiu a troca de dimensão existir sem a ficha pagar nada por ela.
 *
 * Só escolhe: quem grava é o host. O Kanban precisa reagrupar depois, a ficha não, e essa
 * diferença não cabe aqui dentro.
 */
@Component({emits: ['closed', 'pick', 'save', 'update:dimension'],
  name: 'ontos-state-dialog',
  components: {
    'ontos-dialog-actions': DialogActions,
    'ontos-dialog-action-row': DialogActionRow,
    'ontos-colored-value': ColoredValue
  }
})
class StateDialog extends Vue {
  /**
   * O cabeçalho da janela, do jeito que todo modal do app o recebe — ver `utils/heading`.
   *
   * Vem pronto, e não montado aqui, porque quem sabe de que ITEM se trata é quem abriu: o
   * quadro sabe o card, a ficha sabe o item dela, e este componente só sabe de opções.
   */
  @Prop({type: Object as () => Heading, required: true}) readonly heading!: Heading
  @Prop({type: Array as () => ColoredOption[], required: true}) readonly options!: ColoredOption[]
  /**
   * O(s) valor(es) em que o item está, para marcá-los.
   *
   * Aceita as duas formas porque os dois hospedeiros têm formas diferentes à mão: a ficha tem o
   * id da situação, e o Kanban tem o `valuesOf` da dimensão, que sempre devolve lista. O
   * `asArray` resolve isso num lugar só, em vez de uma segunda prop dizendo quase o mesmo.
   */
  @Prop({type: [Number, Array], default: 0}) readonly current!: number | number[]
  /**
   * Em que FORMA os valores se desenham — régua, bolinha ou chip. Ver `ValueForm`.
   *
   * Era um booleano "são itens de coleção?", que dava conta de duas formas e escondia a
   * terceira: opção de domínio e situação de fluxo saíam iguais, as duas com a bolinha, embora
   * o app desenhe a opção como chip em toda outra tela.
   */
  @Prop({type: String as () => ValueForm, default: 'dot'}) readonly form!: ValueForm
  /**
   * O nome do que se está escolhendo: a situação, a prioridade, o responsável.
   *
   * Aparece como rótulo do conteúdo só quando NÃO há o campo de dimensões — ali o nome da
   * escolhida já está dentro dele, e repetir seria dizer a mesma coisa duas vezes.
   */
  @Prop({type: String, default: ''}) readonly field!: string
  /** Entre quais dimensões se pode trocar. Vazio — a ficha — não desenha o campo. */
  @Prop({type: Array as () => DimensionChoice[], default: () => []}) readonly dimensions!: DimensionChoice[]
  @Prop({type: String, default: ''}) readonly dimension!: string
  /** O campo comporta vários valores? Então a escolha é um conjunto, e sai pelo Salvar. */
  @Prop({type: Boolean, default: false}) readonly multiple!: boolean

  /**
   * O rascunho da escolha múltipla. Nasce do que o item já tem e só sai daqui no Salvar.
   *
   * `null` enquanto ninguém mexeu, para a lista seguir o `current` — que muda sozinho quando se
   * troca de dimensão no campo acima. Um array vazio como estado inicial não distinguiria
   * "ainda não mexi" de "desmarquei tudo".
   */
  draft: number[] | null = null

  /** Trocar de dimensão descarta o rascunho: as opções de uma não dizem nada sobre as da outra. */
  @Watch('dimension')
  onDimensionChange() {
    this.draft = null
  }

  get chosen(): number[] {
    return this.draft === null ? asArray(this.current) : this.draft
  }

  isChosen(option: ColoredOption): boolean {
    return this.chosen.includes(option.id)
  }

  toggle(option: ColoredOption) {
    this.draft = this.isChosen(option)
        ? this.chosen.filter(id => id !== option.id)
        : [...this.chosen, option.id]
  }

  /** Na múltipla a linha inteira alterna a caixa; na simples, escolhe e encerra. */
  onPick(option: ColoredOption) {
    if (this.multiple) this.toggle(option)
    else this.$emit('pick', option)
  }

  //Sem rascunho para perder: ESC e clique fora fecham, e não há persistent
  onDialogInput(open: boolean) {
    if (!open) this.$emit('closed')
  }
}

export default toNative(StateDialog)
</script>

<style scoped>
/* Lista longa rola dentro da janela em vez de empurrá-la para fora da tela */
.option-list {
  max-height: 60vh;
  overflow-y: auto;
}
</style>
